import React, { useEffect, useMemo, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompetition } from "@/context/CompetitionContext";
import CompetitionSelector from "@/components/CompetitionSelector";
import { Save, CheckCircle2, FileDown, Printer, Lock } from "lucide-react";
import { ScoreRow } from "@/components/ScoreRow";

const ROUND_LABELS = {
  1: "Eliminatória",
  2: "Físico",
  3: "Coreografia",
  4: "Comparação",
};

export default function Sumulas() {
  const { user } = useAuth();
  const { currentId } = useCompetition();
  const isAdmin = user?.role === "admin";
  const [cats, setCats] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [round, setRound] = useState(1);
  const [judges, setJudges] = useState([]);
  const [judgeId, setJudgeId] = useState(isAdmin ? "" : user?.id);
  const [scores, setScores] = useState({}); // athlete_id -> {nota, obs}
  const [savedScores, setSavedScores] = useState({}); // athlete_id -> score doc (persisted)
  const [history, setHistory] = useState({}); // athlete_id -> audit entries
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [confrontos, setConfrontos] = useState({});

  const currentCat = useMemo(() => cats.find((c) => c.id === categoryId), [cats, categoryId]);
  const locked = !!currentCat?.results_published;

  const loadCats = () =>
    api.get(`/competitions/${currentId}/categories`).then((r) => {
      setCats(r.data);
      setCategoryId((prev) => (r.data.find((c) => c.id === prev) ? prev : r.data[0]?.id || ""));
    });

  // load categories + judges (filtrados pelo campeonato)
  useEffect(() => {
    if (!currentId) return;
    loadCats();
    if (isAdmin) {
      Promise.all([api.get("/users"), api.get(`/competitions/${currentId}`)]).then(([usersRes, compRes]) => {
        const comp = compRes.data;
        const scaledIds = new Set(comp.judge_ids || []);
        const scaled = usersRes.data.filter((u) => u.role === "judge" && scaledIds.has(u.id));
        const merged = comp.admin_is_judge && user
          ? [{ id: user.id, name: `${user.name} (Central)`, role: "admin" }, ...scaled]
          : scaled;
        setJudges(merged);
      });
    } else {
      setJudgeId(user.id);
    }
    // eslint-disable-next-line
  }, [currentId]);

  const loadAthletes = () => {
    if (!currentId || !categoryId) return;
    api
      .get(`/competitions/${currentId}/athletes`, { params: { category_id: categoryId } })
      .then((r) => setAthletes(r.data));
    api
      .get(`/categories/${categoryId}/confrontos`)
      .then((r) => setConfrontos(r.data?.confrontos || {}))
      .catch(() => setConfrontos({}));
  };

  // load athletes + scores for context
  useEffect(() => {
    loadAthletes();
    // eslint-disable-next-line
  }, [currentId, categoryId]);

  const loadScores = () => {
    if (!currentId || !categoryId || !judgeId) {
      setScores({});
      setSavedScores({});
      setHistory({});
      return;
    }
    const params = { competition_id: currentId, category_id: categoryId, round_num: round, judge_id: judgeId };
    api.get("/scores", { params }).then((r) => {
      const m = {};
      const sv = {};
      r.data.forEach((s) => {
        m[s.athlete_id] = { nota: s.nota, obs: s.obs || "" };
        sv[s.athlete_id] = s;
      });
      setScores(m);
      setSavedScores(sv);
    });
    api
      .get("/scores/history", { params })
      .then((r) => {
        const h = {};
        r.data.forEach((e) => (h[e.athlete_id] = [...(h[e.athlete_id] || []), e]));
        setHistory(h);
      })
      .catch(() => setHistory({}));
  };

  useEffect(() => {
    loadScores();
    // eslint-disable-next-line
  }, [currentId, categoryId, round, judgeId]);

  const save = async () => {
    setError("");
    setSaved(false);
    try {
      const woIds = new Set(athletes.filter((a) => a.wo).map((a) => a.id));
      const payload = {
        competition_id: currentId,
        category_id: categoryId,
        round_num: round,
        judge_id: judgeId,
        scores: Object.entries(scores)
          .filter(([aid, v]) => !woIds.has(aid) && v.nota !== "" && v.nota != null)
          .map(([athlete_id, v]) => ({ athlete_id, nota: Number(v.nota), obs: v.obs || "" })),
      };
      await api.post("/scores/batch", payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      loadScores();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  const removeScore = async (score) => {
    const a = athletes.find((x) => x.id === score.athlete_id);
    if (!window.confirm(`Remover a nota ${score.nota} de ${a?.nome || "este atleta"} no R${round}?\nO atleta continua inscrito na categoria, apenas sem nota nesta rodada.`)) return;
    setError("");
    try {
      await api.delete(`/scores/${score.id}`);
      loadScores();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  const toggleWo = async (a) => {
    const msg = a.wo
      ? `Desfazer W.O. de ${a.nome}? O atleta volta a contar no ranking e no roteiro.`
      : `Marcar ${a.nome} como W.O. (ausente do palco)?\nEle sai do ranking da categoria e deixa de contar tempo no roteiro.`;
    if (!window.confirm(msg)) return;
    setError("");
    try {
      await api.patch(`/athletes/${a.id}/wo`, { wo: !a.wo });
      loadAthletes();
      loadScores();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  const canSave = judgeId && categoryId && athletes.length > 0 && !locked;

  const exportSumulaPdf = () => {
    if (!judgeId || !categoryId) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/sumula.pdf?competition_id=${currentId}&category_id=${categoryId}&round_num=${round}&judge_id=${judgeId}`;
    window.open(url, "_blank");
  };

  const exportSumulaBlankPdf = () => {
    if (!categoryId) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/sumula-blank.pdf?competition_id=${currentId}&category_id=${categoryId}&round_num=${round}`;
    window.open(url, "_blank");
  };

  const missing = athletes.filter((a) => !a.wo && !savedScores[a.id]).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">Julgamento</div>
          <h1 className="font-display text-5xl font-black tracking-tight">SÚMULAS</h1>
        </div>
        <CompetitionSelector />
      </div>

      <div className="card p-5 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="overline block mb-2">Categoria</label>
            <select
              data-testid="sumula-categoria"
              className="input-field"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">Selecione...</option>
              {cats.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.results_published ? "🔒 " : ""}{c.nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="overline block mb-2">Round</label>
            <select
              data-testid="sumula-round"
              className="input-field"
              value={round}
              onChange={(e) => setRound(parseInt(e.target.value))}
            >
              {[1, 2, 3, 4].map((r) => (
                <option key={r} value={r}>
                  R{r} — {ROUND_LABELS[r]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="overline block mb-2">Árbitro</label>
            {isAdmin ? (
              <select
                data-testid="sumula-arbitro"
                className="input-field"
                value={judgeId}
                onChange={(e) => setJudgeId(e.target.value)}
              >
                <option value="">Selecione o árbitro</option>
                {judges.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.email ? `${j.name} (${j.email})` : j.name}
                  </option>
                ))}
              </select>
            ) : (
              <div className="input-field bg-[#0A0A0A]">{user?.name}</div>
            )}
          </div>
          <div className="flex items-end gap-2">
            <button
              data-testid="sumula-save"
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              disabled={!canSave}
              onClick={save}
            >
              <Save size={16} /> Salvar
            </button>
            <button
              data-testid="sumula-pdf"
              className="btn-ghost flex items-center justify-center gap-2"
              disabled={!judgeId || !categoryId || athletes.length === 0}
              onClick={exportSumulaPdf}
              title="Baixar súmula preenchida em PDF"
            >
              <FileDown size={16} />
            </button>
            <button
              data-testid="sumula-blank-pdf"
              className="btn-ghost flex items-center justify-center gap-2"
              disabled={!categoryId || athletes.length === 0}
              onClick={exportSumulaBlankPdf}
              title="Baixar súmula em branco (impressão)"
            >
              <Printer size={16} />
            </button>
          </div>
        </div>
        {locked && (
          <div
            data-testid="sumula-locked-banner"
            className="mt-4 flex items-center gap-2 text-sm text-[#C9A84C] border border-[#C9A84C]/40 bg-[#C9A84C]/10 px-3 py-2"
          >
            <Lock size={15} />
            <span>
              <b>Resultado fechado.</b> Esta categoria está publicada — notas bloqueadas para edição, remoção e W.O.
              {isAdmin && " Para reabrir, despublique o resultado em Classificação."}
            </span>
          </div>
        )}
        {saved && (
          <div className="mt-3 text-[var(--success)] text-sm flex items-center gap-2">
            <CheckCircle2 size={16} /> Notas salvas com sucesso.
          </div>
        )}
        {error && (
          <div data-testid="sumula-error" className="mt-3 text-sm text-[#C9A84C] border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-3 py-2">
            {error}
          </div>
        )}
      </div>

      <div className="card overflow-x-auto">
        <table className="table-dense">
          <thead>
            <tr>
              <th className="w-14">Nº</th>
              <th>Atleta</th>
              <th>Equipe</th>
              <th className="w-32">Nota</th>
              <th>Observação</th>
              <th className="w-24 text-right pr-2">Ações</th>
            </tr>
          </thead>
          <tbody>
            {athletes.map((a) => (
              <ScoreRow
                key={a.id}
                a={a}
                cur={scores[a.id] || { nota: "", obs: "" }}
                saved={savedScores[a.id]}
                history={history[a.id]}
                confrontos={confrontos[a.id]}
                locked={locked}
                isAdmin={isAdmin}
                judgeId={judgeId}
                onChange={(v) => setScores({ ...scores, [a.id]: v })}
                onRemove={removeScore}
                onToggleWo={toggleWo}
              />
            ))}
            {athletes.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-8 text-[var(--text-secondary)]">
                  Nenhum atleta nesta categoria.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-[var(--text-secondary)] mt-3 flex flex-wrap gap-x-6 gap-y-1">
        <span>
          Nota padrão: 1 (melhor) a n (pior). Quando houver 5+ árbitros, a maior e a menor nota
          são descartadas automaticamente no cálculo da classificação.
        </span>
        {judgeId && athletes.length > 0 && (
          <span data-testid="sumula-missing-count">
            {missing === 0 ? "Todos os atletas com nota neste round." : `${missing} atleta(s) sem nota neste round.`}
          </span>
        )}
      </div>
    </div>
  );
}
